/**
 * @architecture-header 1
 * @layer routes
 * @domain workspace-shell
 * @responsibility Build the student list column for every section.
 * @state-owner navigation store; dataset store
 * @allowed-imports React; workspace and dataset stores; shared components, selectors and hooks
 * @forbidden API client; in-product role switching
 * @public-api ListColumn
 * @side-effects navigation only
 * @tests workspace Playwright flows in frontend/apps/web/e2e/workspace.spec.ts
 */

import { ListEmpty, ListGroup, ListRow } from '../../../shared/components/ListColumn';
import { RUN_MODE_LABEL } from '../../../shared/lib/demo-data';
import {
  classRuns,
  myClassIndexes,
  myRuns,
  runName,
  studentRunState,
} from '../../../shared/lib/selectors';
import { useDatasetStore } from '../../../shared/model/dataset-store';
import { useIsMobile, useTranslator, useWorkspacePick } from '../../../shared/model/use-workspace';
import { useWorkspaceUiStore } from '../../../shared/model/workspace-store';
import type { ListContent } from '../ListColumn';

export function StudentListBody(): ListContent {
  const t = useTranslator();
  const mobile = useIsMobile();
  const section = useWorkspaceUiStore((state) => state.section);
  const selection = useWorkspaceUiStore((state) => state.selection);
  const studentTab = useWorkspaceUiStore((state) => state.studentTab);
  const filters = useWorkspaceUiStore((state) => state.filters);
  const pick = useWorkspacePick();
  const navigate = useWorkspaceUiStore((state) => state.navigate);
  const classes = useDatasetStore((state) => state.classes);
  const activities = useDatasetStore((state) => state.activities);
  const runs = useDatasetStore((state) => state.runs);

  const mine = myClassIndexes(classes);
  const mineRuns = myRuns(classes, runs);

  if (section === 'room') {
    const todoOf = (classIndex: number) =>
      mineRuns.filter(
        (run) => run.classIndex === classIndex && (run.status === 'live' || run.status === 'open'),
      ).length;
    return {
      title: t('我的班級'),
      subtitle: `${mine.length} ${t('個班')}`,
      body: (
        <>
          {mine.length ? (
            <ListGroup id="s-c" label={t('我在的班')}>
              {mine.map((classIndex) => {
                const item = classes[classIndex];
                const live = classRuns(runs, classIndex).some((run) => run.status === 'live');
                const todo = todoOf(classIndex);
                return (
                  <ListRow
                    background={live ? 'var(--primary-pale)' : undefined}
                    flag={live ? '上課中' : undefined}
                    foreground={live ? 'var(--primary)' : undefined}
                    icon={t('班字')}
                    key={item.id}
                    on={selection === `c${classIndex}`}
                    onSelect={() => pick(`c${classIndex}`)}
                    subtitle={item.subtitle}
                    time={live ? '' : todo ? `${todo} 項待完成` : ''}
                    title={item.name}
                  />
                );
              })}
            </ListGroup>
          ) : (
            <ListEmpty
              description={t('加入班級後，老師指派的活動會顯示在這裡。')}
              title={t('還沒有班級')}
            />
          )}
        </>
      ),
    };
  }

  if (section === 'act') {
    const active = filters.sact?.f ?? [];
    const classFilter = filters.sact?.ci ?? [];
    const rows = mineRuns.filter(
      (run) =>
        (!active.length || active.includes(studentRunState(run))) &&
        (!classFilter.length || classFilter.includes(String(run.classIndex))),
    );
    const todo = rows.filter((run) => run.status === 'live' || run.status === 'open');
    return {
      title: t('我的作業'),
      subtitle: `${todo.length} ${t('項待完成')}`,
      body: (
        <>
          {mobile ? (
            <ListGroup id="s-home" label={t('我的學習')}>
              <ListRow
                icon={t('班字')}
                onSelect={() => navigate('room', null)}
                subtitle={t('查看班級與學習活動')}
                title={t('我的班級')}
              />
              <ListRow
                icon={t('報字')}
                onSelect={() => navigate('rep', null)}
                subtitle={t('查看學習報告')}
                title={t('我的報告')}
              />
            </ListGroup>
          ) : null}
          {rows.length ? (
            mine.map((classIndex) => {
              const group = rows.filter((run) => run.classIndex === classIndex);
              if (!group.length) return null;
              return (
                <ListGroup
                  count={group.length}
                  id={`s-a-${classIndex}`}
                  key={classes[classIndex].id}
                  label={classes[classIndex].name}
                >
                  {group.map((run) => (
                    <ListRow
                      flag={
                        run.status === 'live'
                          ? t('進行中')
                          : run.status === 'open'
                            ? t('還沒交')
                            : undefined
                      }
                      icon={t('題字')}
                      key={run.id}
                      on={selection === run.id}
                      onSelect={() => pick(run.id)}
                      subtitle={`${t(RUN_MODE_LABEL[run.mode])} · ${t(run.dateLabel)}`}
                      time={run.status === 'ended' ? '已完成' : ''}
                      title={runName(activities, run)}
                    />
                  ))}
                </ListGroup>
              );
            })
          ) : (
            <ListEmpty
              description={
                studentTab === 'todo'
                  ? '老師指派新的活動後，就會顯示在這裡。'
                  : '完成並送出活動後，就會顯示在這裡。'
              }
              title={studentTab === 'todo' ? '目前沒有待完成的活動' : t('還沒有交出去的活動')}
            />
          )}
        </>
      ),
    };
  }

  const ended = mineRuns.filter((run) => run.status === 'ended');
  return {
    title: t('我的報告'),
    subtitle: `${ended.length} ${t('次上課')}`,
    body: ended.length ? (
      mine.map((classIndex) => {
        const group = ended.filter((run) => run.classIndex === classIndex);
        if (!group.length) return null;
        return (
          <ListGroup
            count={group.length}
            id={`s-r-${classIndex}`}
            key={classes[classIndex].id}
            label={classes[classIndex].name}
          >
            {group.map((run) => (
              <ListRow
                icon={t('報字')}
                key={run.id}
                on={selection === run.id}
                onSelect={() => pick(run.id)}
                subtitle={t(RUN_MODE_LABEL[run.mode])}
                time="9 / 12"
                title={`${t(run.dateLabel)} · ${runName(activities, run)}`}
              />
            ))}
          </ListGroup>
        );
      })
    ) : (
      <ListEmpty
        description={t('完成老師指派的活動後，學習報告會顯示在這裡。')}
        title={t('還沒有報告')}
      />
    ),
  };
}
